import {
  DEFAULT_PAGE_SIZE,
  StorageAdapter,
  StorageCapabilities,
  FeedItemUrlMap,
  StorageItemId,
} from '../common/adapter';
import pLimit from 'p-limit';
import { StorageManager } from '../common/manager';
import { DropboxListPage } from './dropbox.list-page';
import { Dropbox, DropboxAuth } from 'dropbox';
import { StorageGetNextListPage, StorageListPage } from '../common/list-page';
import {
  BasicStorageItem,
  isNonEmptyString,
  Logger,
  NotFoundError,
  ServiceProvider,
  StorageFolder,
  StorageItem,
} from '@uploadcam/common';
import { DropboxFile, DropboxItem } from './dropbox.item';
import { MockItem } from '../common/mock.item';
import { MockListPage } from '../common/mock.list-page';
import invariant from 'tiny-invariant';
import { FriendlyError, waitExpBackoff } from '../../../../app/core/utils';

interface VirtualRoot {
  id: typeof DROPBOX_VIRTUAL_ROOT;
  allowsSubfolderCreation: false;
  teamRoot: StorageFolder;
}

interface RealRoot {
  id: typeof DROPBOX_REAL_ROOT;
  allowsSubfolderCreation: boolean;
}

type Root = VirtualRoot | RealRoot;

export const DROPBOX_VIRTUAL_ROOT = 'uploadcam://dropbox/virtual-root';
export const DROPBOX_REAL_ROOT = 'uploadcam://dropbox/real-root';

export class DropboxAdapter implements StorageAdapter {
  private manager!: StorageGetNextListPage;
  private readonly root: Root;

  constructor(
    private readonly dbx: Dropbox,
    private readonly dbxAuth: DropboxAuth,
    private readonly logger: Logger,
    public readonly isTeam: boolean,
    teamRoot?: StorageFolder,
  ) {
    if (teamRoot) {
      // Team Created, logged in and inside the web-app
      this.root = {
        id: DROPBOX_VIRTUAL_ROOT,
        allowsSubfolderCreation: false,
        teamRoot,
      };
    } else if (isTeam) {
      // Onboarding Dropbox Team
      this.root = {
        id: DROPBOX_REAL_ROOT,
        allowsSubfolderCreation: false,
      };
    } else {
      // Onboarding Dropbox Personal
      this.root = {
        id: DROPBOX_REAL_ROOT,
        allowsSubfolderCreation: true,
      };
    }
  }

  public setManager(manager: StorageManager): void {
    this.manager = manager as unknown as StorageGetNextListPage;
  }

  public getCapabilities(): StorageCapabilities {
    return {
      previewThumbnails: true,
      userGroups: false,
    };
  }

  public getProvider(): ServiceProvider {
    return ServiceProvider.Dropbox;
  }

  public async getRootFolder(): Promise<StorageItem> {
    return MockItem.rootFolder(
      this.root.id,
      ServiceProvider.Dropbox,
      undefined,
      this.root.allowsSubfolderCreation,
    );
  }

  public async getDownloadUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    const limit = pLimit(4);
    const tasks = items.map<Promise<[string, string | undefined]>>((item) =>
      limit(async () => {
        try {
          const res = await this.dbx.filesGetTemporaryLink({ path: item.id });

          if (res.result.link) {
            return [item.id, res.result.link];
          } else {
            return [item.id, undefined];
          }
        } catch (err) {
          this.logger.error(err);
          return [item.id, undefined];
        }
      }),
    );

    return Object.fromEntries(await Promise.all(tasks));
  }

  public async getFileUrl(file: StorageItem): Promise<string> {
    const res = await this.dbx.filesGetMetadata({ path: file.id });

    if (res.result['.tag'] === 'file') {
      return `https://www.dropbox.com/preview${res.result.path_display}`;
    }

    throw new NotFoundError();
  }

  public async getFolderUrl(folder: StorageItem): Promise<string> {
    const res = await this.dbx.filesGetMetadata({ path: folder.id });

    if (res.result['.tag'] === 'folder') {
      return `https://www.dropbox.com/home${res.result.path_display}`;
    }

    throw new NotFoundError();
  }

  public getProviderUrl(): string {
    return `https://www.dropbox.com/home`;
  }

  public async getImageUrls(
    items: BasicStorageItem[],
    size: 'w640h480' | 'w2048h1536',
  ): Promise<FeedItemUrlMap> {
    const thumbnailUrls: FeedItemUrlMap = {};
    const batchRequests: Parameters<Dropbox['filesGetThumbnailBatch']>[0]['entries'] = [];

    for (const item of items) {
      thumbnailUrls[item.id] = undefined;

      batchRequests.push({
        path: item.id,
        size: { '.tag': size },
      });
    }

    const { result } = await this.dbx.filesGetThumbnailBatch({
      entries: batchRequests,
    });

    for (const entry of result.entries) {
      if (entry['.tag'] === 'success') {
        thumbnailUrls[entry.metadata.id] = `data:image/jpeg;base64,${entry.thumbnail}`;
      }
    }

    return thumbnailUrls;
  }

  public async getThumbnailUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    return this.getImageUrls(items, 'w640h480');
  }

  public async getPreviewUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    return this.getImageUrls(items, 'w2048h1536');
  }

  public updateAccessToken(token: string): void {
    this.dbxAuth.setAccessToken(token);
  }

  public async write(parent: StorageItem, file: File): Promise<StorageItem> {
    let path = '';

    if (parent.id) {
      path = await this.getFullPath(parent.id);
    }

    const { result } = await this.dbx.filesUpload({
      path: `${path}/${file.name}`,
      contents: file,
      // Match the behaviour of the other providers (OneDrive uses
      // `conflictBehavior: rename`): a name clash should not abort the upload.
      autorename: true,
    });

    return new DropboxItem({
      ...result,
      '.tag': 'file',
    });
  }

  private async getFullPath(itemId: string): Promise<string> {
    const res = await this.dbx.filesGetMetadata({ path: itemId });
    return res.result.path_lower ?? '';
  }

  public async createFolder(parent: DropboxItem, name: string): Promise<DropboxItem> {
    const path = parent.id === DROPBOX_REAL_ROOT ? '' : await this.getFullPath(parent.id);
    const res = await this.dbx.filesCreateFolderV2({
      path: `${path}/${name}`,
    });

    return new DropboxItem({
      ...res.result.metadata,
      '.tag': 'folder',
    });
  }

  public async renameFolder(folder: DropboxItem, name: string): Promise<DropboxItem> {
    const currentPath = await this.getFullPath(folder.id);
    const lastSlash = currentPath.lastIndexOf('/');
    const parentPath = lastSlash > 0 ? currentPath.slice(0, lastSlash) : '';
    const newPath = `${parentPath}/${name}`;

    await this.assertPathAvailable(newPath, name, folder.id);

    const res = await this.dbx.filesMoveV2({
      from_path: folder.id,
      to_path: newPath,
      // Do not silently append a suffix on conflict; we surface an error instead.
      autorename: false,
    });

    return new DropboxItem({
      ...res.result.metadata,
      '.tag': 'folder',
    } as DropboxFile);
  }

  private async assertPathAvailable(path: string, name: string, selfId: string): Promise<void> {
    let existingId: string | undefined;

    try {
      const res = await this.dbx.filesGetMetadata({ path });

      // A `deleted` entry means the path is free to reuse.
      if (res.result['.tag'] === 'deleted') {
        return;
      }

      existingId = res.result.id;
    } catch (err) {
      // A `not_found` error means the path is free, which is what we want.
      if (this.isPathNotFoundError(err)) {
        return;
      }

      throw err;
    }

    // Dropbox paths are case-insensitive, so a case-only rename resolves to the
    // same folder. Only treat it as a conflict when a *different* item is there.
    if (existingId !== selfId) {
      throw new FriendlyError(
        'folderNameConflict',
        new Error(`A folder named "${name}" already exists.`),
      );
    }
  }

  private isPathNotFoundError(err: unknown): boolean {
    const summary = (err as { error?: { error_summary?: unknown } })?.error?.error_summary;
    return typeof summary === 'string' && summary.includes('not_found');
  }

  public async listFolder(
    item: DropboxItem,
    onlyFolders: boolean = false,
  ): Promise<StorageListPage> {
    if (item.id === DROPBOX_VIRTUAL_ROOT && this.root.id === DROPBOX_VIRTUAL_ROOT) {
      return new MockListPage([
        MockItem.fromStorageFolder(this.root.teamRoot, true, 'Team Root'),
        MockItem.folder(
          DROPBOX_REAL_ROOT,
          ServiceProvider.Dropbox,
          undefined,
          'Dropbox Root',
          !this.isTeam,
        ),
      ]);
    }

    const isRealRoot = item.id === DROPBOX_REAL_ROOT;
    let dbx = this.dbx;
    let path = item.id;

    if (isRealRoot) {
      // '' is the root folder, see 'Path formats': https://www.dropbox.com/developers/documentation/http/documentation
      path = '';
    }

    const res = await dbx.filesListFolder({
      path,
      limit: DEFAULT_PAGE_SIZE,
    });

    // Dropbox's list API returns entries in an unspecified (non-alphabetical)
    // order and paginates over *all* entries before any folder filtering. There
    // is no server-side ordering option, so page boundaries don't respect the
    // alphabet: the alphabetically-first entry can live on the last page. To
    // return a correctly sorted list we must drain every page here and sort the
    // complete set once. We then collapse it into a single, non-paginated page
    // (`has_more = false`) so the caller renders it all at once, in order.
    while (res.result.has_more) {
      const next = await dbx.filesListFolderContinue({ cursor: res.result.cursor });
      res.result.entries.push(...next.result.entries);
      res.result.has_more = next.result.has_more;
      res.result.cursor = next.result.cursor;
    }

    if (onlyFolders) {
      res.result.entries = res.result.entries.filter((entry) => entry['.tag'] === 'folder');
    }

    // Sort the entries by name in ascending order
    res.result.entries.sort((a, b) => a.name.localeCompare(b.name));

    return new DropboxListPage(this.manager, res.result, onlyFolders);
  }

  public async moveItems(
    targetFolder: StorageItem,
    items: StorageItem[],
  ): Promise<Record<StorageItemId, boolean>> {
    const targetMeta = await this.dbx.filesGetMetadata({ path: targetFolder.id });

    if (targetMeta.result['.tag'] !== 'folder') {
      throw new Error('Target is not a folder');
    }

    const toPath = targetMeta.result.path_lower;
    invariant(isNonEmptyString(toPath));

    const job = await this.dbx.filesMoveBatchV2({
      autorename: true,
      entries: items.map(({ id, name }) => ({
        from_path: id,
        to_path: `${toPath}/${name}`,
      })),
    });

    // Inner function so we can correctly type check `jobResult`
    const getJobResult = async (job: Awaited<ReturnType<Dropbox['filesMoveBatchV2']>>) => {
      if (job.result['.tag'] === 'async_job_id') {
        const maxRetries = 30;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          const pollResult = await this.dbx.filesMoveBatchCheckV2({
            async_job_id: job.result.async_job_id,
          });

          if (pollResult.result['.tag'] === 'in_progress') {
            await waitExpBackoff({ attempt, baseDelayMs: 250, maxDelayMs: 2000 });
          } else {
            return pollResult.result;
          }
        }

        return undefined;
      } else {
        return job.result;
      }
    };

    const jobResult = await getJobResult(job);

    if (!jobResult || jobResult['.tag'] !== 'complete') {
      this.logger.warn('Dropbox move batch job failed or timed out.', jobResult);
      return Object.fromEntries(items.map((item) => [item.id, false]));
    }

    return Object.fromEntries(
      jobResult.entries.map((entry, index) => [items[index].id, entry['.tag'] === 'success']),
    );
  }

  public async trashItems(items: StorageItem[]): Promise<Record<StorageItemId, boolean>> {
    if (items.length === 0) {
      return {};
    }

    // Initialize all as failed, update on success
    const result: Record<StorageItemId, boolean> = {};
    items.forEach((item) => (result[item.id] = false));

    const job = await this.dbx.filesDeleteBatch({
      entries: items.map((item) => ({ path: item.id })),
    });

    const getJobResult = async (job: Awaited<ReturnType<Dropbox['filesDeleteBatch']>>) => {
      if (job.result['.tag'] === 'async_job_id') {
        const maxRetries = 30;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          const pollResult = await this.dbx.filesDeleteBatchCheck({
            async_job_id: job.result.async_job_id,
          });

          if (pollResult.result['.tag'] === 'in_progress') {
            await waitExpBackoff({ attempt, baseDelayMs: 250, maxDelayMs: 2000 });
          } else {
            return pollResult.result;
          }
        }

        return undefined;
      } else if (job.result['.tag'] === 'complete') {
        return job.result;
      } else {
        return undefined;
      }
    };

    const jobResult = await getJobResult(job);

    if (jobResult && jobResult['.tag'] === 'complete') {
      jobResult.entries.forEach((entry) => {
        if (entry['.tag'] === 'success') {
          const metadata = entry.metadata;

          if (metadata['.tag'] !== 'deleted') {
            const originalItem = items.find((i) => i.id === metadata.id);

            if (originalItem) {
              result[originalItem.id] = true;
            }
          }
        }
      });
    } else {
      this.logger.warn('Dropbox move batch job failed or timed out.', jobResult);
    }

    return result;
  }

  // Note: `listFolder` now drains all pages and returns a single page with
  // `has_more = false`, so this is not reached by the folder picker flow. It is
  // kept for interface compliance and sorts defensively — Dropbox pages are not
  // globally ordered, so a lone continuation page can only be sorted in
  // isolation (correct only if paging is ever re-enabled with a stable order).
  public async getNextFolderPage(
    onlyFolders: boolean,
    options: { cursor: string },
  ): Promise<StorageListPage> {
    const res = await this.dbx.filesListFolderContinue({
      cursor: options.cursor,
    });

    if (onlyFolders) {
      res.result.entries = res.result.entries.filter((entry) => entry['.tag'] === 'folder');
    }

    res.result.entries.sort((a, b) => a.name.localeCompare(b.name));

    return new DropboxListPage(this.manager, res.result, onlyFolders);
  }

  public async setCaption(item: Readonly<StorageItem>, caption?: string): Promise<void> {}
}
